import {
  ArrowBackIosNew,
  CodeOutlined,
  HistoryOutlined,
  HomeOutlined,
  OpenInNew,
  PaymentsRounded,
  SettingsSuggestOutlined,
  SpeedOutlined,
  ViewCozyOutlined,
} from '@mui/icons-material'
import { SvgIcon } from '@mui/material'
import { generatePath, Params, To } from 'react-router-dom'

import { Product, productTypeValues } from 'features/product/types/product'
import AbTestsIcon from 'images/icons/ab-tests.svg?react'
import GoogleAdManagerIcon from 'images/icons/google-ad-manager.svg?react'
import { theme } from 'providers/material-ui'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { SidebarItem } from '../types'
import {
  canAccessPerformanceProductGroup,
  performanceProductItems,
} from './performanceProductItems'
import { ConfigGeneralProps } from './types'

export const configGeneral = ({
  ability,
  navigate,
  networkCode,
  t,
  tProduct,
  updateCurrentWorkspace,
  workspaceId,
}: ConfigGeneralProps): SidebarItem[] => {
  const withWorkspace = (path: string, params?: Params): To =>
    generatePath(path, { workspaceId, ...params })

  const BACK_TO_WORKSPACES: SidebarItem = {
    color: theme.palette.cream,
    icon: <ArrowBackIosNew />,
    isSelectable: false,
    marginBottom: true,
    onClick: async () => {
      const status = await updateCurrentWorkspace(undefined)
      if (status === 'failure') return

      navigate(ROUTES.WORKSPACE.LIST)
    },
    title: t('sidebar.employee.backToWorkspaces'),
    to: ROUTES.WORKSPACE.LIST,
  }

  const canAccessInventoryGroup: boolean =
    ability.can('access', 'DomainPage-list') ||
    ability.can('access', 'AdUnitPage-list') ||
    ability.can('access', 'PagePage-list')

  const INVENTORY_GROUP: SidebarItem = {
    group: 'inventory',
    icon: <ViewCozyOutlined />,
    productFruitsSelector: generateSelector({ step: 3, tour: 1 }),
    subItems: [
      {
        hidden: ability.cannot('access', 'DomainPage-list'),
        title: t('sidebar.general.inventory.domains'),
        to: withWorkspace(ROUTES.INVENTORY.DOMAIN.LIST),
      },
      {
        hidden: ability.cannot('access', 'AdUnitPage-list'),
        title: t('sidebar.general.inventory.adUnits'),
        to: withWorkspace(ROUTES.INVENTORY.AD_UNIT.LIST),
      },
      {
        hidden: ability.cannot('access', 'PagePage-list'),
        title: t('sidebar.general.inventory.pages'),
        to: withWorkspace(ROUTES.INVENTORY.PAGE.LIST),
      },
    ],
    title: t('sidebar.general.inventory.title'),
  }

  const canAccessPerformanceGroup: boolean =
    ability.can('access', 'PerformanceGeneralPage') ||
    canAccessPerformanceProductGroup(ability, productTypeValues)

  const PERFORMANCE_PREBID_STACK_GROUP: SidebarItem = {
    group: 'performance-prebidStack',
    hidden:
      ability.cannot('access', 'Product-prebidStack') ||
      ability.cannot('access', 'PerformancePrebidStackPage'),
    subItems: [
      {
        shifted: true,
        title: t('sidebar.general.performance.prebidStack.analytics'),
        to: withWorkspace(ROUTES.PERFORMANCE.PREBID_STACK.ANALYTICS, { tab: 'total' }),
      },
      {
        shifted: true,
        title: t('sidebar.general.performance.prebidStack.ybBidders'),
        to: withWorkspace(ROUTES.PERFORMANCE.ROOT + '/prebid-stack'),
      },
    ],
    title: t('sidebar.general.performance.prebidStack.title'),
  }

  const PERFORMANCE_GROUP: SidebarItem = {
    group: 'performance',
    icon: <SpeedOutlined />,
    productFruitsSelector: generateSelector({ step: 4, tour: 1 }),
    subItems: [
      {
        hidden: ability.cannot('access', 'PerformanceGeneralPage'),
        id: 'performance-general',
        title: t('sidebar.general.performance.general'),
        to: withWorkspace(ROUTES.PERFORMANCE.GENERAL),
      },
      ...(ability.can('access', 'Product-prebidStack') &&
      ability.can('access', 'PerformancePrebidStackPage')
        ? [PERFORMANCE_PREBID_STACK_GROUP]
        : []),
      ...performanceProductItems({
        ability,
        products: productTypeValues.filter((p: Product) => p !== 'prebidStack'),
        t: tProduct,
        workspaceId,
      }),
    ],
    title: t('sidebar.general.performance.title'),
  }

  const canAccessGlobalSetupDefaults: boolean =
    ability.can('access', 'GlobalSetupPage-defaults-general') ||
    ability.can('access', 'GlobalSetupPage-defaults-prebidStack') ||
    ability.can('access', 'GlobalSetupPage-defaults-richPlacements')

  const canAccessGlobalSetupGroup: boolean =
    canAccessGlobalSetupDefaults ||
    ability.can('access', 'Product-prebidStack') ||
    ability.can('access', 'GlobalSetupPage-amazon') ||
    ability.can('access', 'GlobalSetupPage-supplyChain-list') ||
    ability.can('access', 'GlobalSetupPage-viewport-list')

  const canAccessGlobalSetupPrebiGroup: boolean =
    ability.can('access', 'Product-prebidStack') &&
    (ability.can('access', 'GlobalSetupPage-prebid-protection-list') ||
      ability.can('access', 'GlobalSetupPage-prebid-modules') ||
      ability.can('access', 'GlobalSetupPage-prebid-bidders'))

  const GLOBAL_SETUP_GROUP: SidebarItem = {
    group: 'globalSetup',
    icon: <SettingsSuggestOutlined />,
    productFruitsSelector: generateSelector({ step: 5, tour: 1 }),
    subItems: [
      {
        hidden: !canAccessGlobalSetupDefaults,
        selectablePaths: [
          withWorkspace(ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT, { tab: 'general' }),
          withWorkspace(ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT, { tab: 'prebid-stack' }),
          withWorkspace(ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT, { tab: 'rich-placements' }),
        ],
        title: t('sidebar.general.globalSetup.defaults'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT, {
          tab: ability.can('access', 'GlobalSetupPage-defaults-general')
            ? 'general'
            : 'prebid-stack',
        }),
      },
      {
        group: 'globalSetup-prebid',
        hidden: !canAccessGlobalSetupPrebiGroup,
        subItems: [
          {
            hidden: ability.cannot('access', 'GlobalSetupPage-prebid-bidders'),
            shifted: true,
            title: t('sidebar.general.globalSetup.prebid.bidders'),
            to: withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.BIDDERS.ROOT),
          },
          {
            hidden: ability.cannot('access', 'GlobalSetupPage-prebid-modules'),
            selectablePaths: [
              withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.MODULES.ROOT, { tab: 'general' }),
              withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.MODULES.ROOT, { tab: 'userId' }),
              withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.MODULES.ROOT, { tab: 'vendorSpecific' }),
              withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.MODULES.ROOT, { tab: 'analytics' }),
            ],
            shifted: true,
            title: t('sidebar.general.globalSetup.prebid.modules'),
            to: withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.MODULES.ROOT, { tab: 'general' }),
          },
          {
            hidden: ability.cannot('access', 'GlobalSetupPage-prebid-protection-list'),
            shifted: true,
            title: t('sidebar.general.globalSetup.prebid.protection'),
            to: withWorkspace(ROUTES.GLOBAL_SETUP.PREBID.PROTECTION.LIST),
          },
        ],
        title: t('sidebar.general.globalSetup.prebid.title'),
      },
      {
        hidden: ability.cannot('access', 'GlobalSetupPage-amazon'),
        selectablePaths: [
          withWorkspace(ROUTES.GLOBAL_SETUP.AMAZON.ROOT, { tab: 'general' }),
          withWorkspace(ROUTES.GLOBAL_SETUP.AMAZON.ROOT, { tab: 'inventory-connection' }),
        ],
        title: t('sidebar.general.globalSetup.amazon'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.AMAZON.ROOT, { tab: 'general' }),
      },
      {
        hidden: ability.cannot('access', 'GlobalSetupPage-supplyChain-list'),
        title: t('sidebar.general.globalSetup.schain'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.SCHAIN.LIST),
      },
      {
        hidden: ability.cannot('access', 'GlobalSetupPage-viewport-list'),
        title: t('sidebar.general.globalSetup.viewports'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.VIEWPORT),
      },
      {
        hidden: ability.cannot('access', 'GlobalSetupPage-googleAdManagerConnection'),
        title: t('sidebar.general.globalSetup.gamConnection'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.GAM_CONNECTION.ROOT),
      },
      {
        hidden: ability.cannot('access', 'GlobalSetupPage-general'),
        selectablePaths: [
          withWorkspace(ROUTES.GLOBAL_SETUP.GENERAL.ROOT, { tab: 'workspace' }),
          withWorkspace(ROUTES.GLOBAL_SETUP.GENERAL.ROOT, { tab: 'user-management' }),
        ],
        title: t('sidebar.general.globalSetup.general'),
        to: withWorkspace(ROUTES.GLOBAL_SETUP.GENERAL.ROOT, { tab: 'workspace' }),
      },
    ],
    title: t('sidebar.general.globalSetup.title'),
  }

  const GAM_ACCOUNT_ITEM: SidebarItem = {
    endAdornment: <OpenInNew fontSize='inherit' />,
    icon: (
      <SvgIcon
        component={GoogleAdManagerIcon}
        inheritViewBox
      />
    ),
    newTab: true,
    productFruitsSelector: generateSelector({ step: 8, tour: 1 }),
    title: t('sidebar.external.googleadManagerAccount'),
    to: `https://admanager.google.com/${networkCode}#home`,
  }

  return [
    ...(ability.can('access', 'WorkspacePage-list') ? [BACK_TO_WORKSPACES] : []),

    {
      hidden: ability.cannot('access', 'ProductPage-list'),
      icon: <HomeOutlined />,
      productFruitsSelector: generateSelector({ step: 2, tour: 1 }),
      title: t('sidebar.general.home'),
      to: withWorkspace(ROUTES.PRODUCT.LIST),
    },
    ...(canAccessInventoryGroup ? [INVENTORY_GROUP] : []),
    ...(canAccessPerformanceGroup ? [PERFORMANCE_GROUP] : []),
    ...(canAccessGlobalSetupGroup ? [GLOBAL_SETUP_GROUP] : []),

    {
      hidden: ability.cannot('access', 'TagPage-download'),
      icon: <CodeOutlined />,
      productFruitsSelector: generateSelector({ step: 6, tour: 1 }),
      title: t('sidebar.general.downloadTags'),
      to: withWorkspace(ROUTES.TAG),
    },
    {
      hidden: ability.cannot('access', 'AbTestsPage-list'),
      icon: <AbTestsIcon />,
      title: t('sidebar.general.abTests'),
      to: withWorkspace(ROUTES.AB_TESTS.LIST),
    },
    {
      hidden: ability.cannot('access', 'FinancePage'),
      icon: <PaymentsRounded />,
      productFruitsSelector: generateSelector({ step: 7, tour: 1 }),
      title: t('sidebar.general.finance'),
      to: withWorkspace(ROUTES.FINANCE),
    },
    {
      hidden: ability.cannot('access', 'HistoryPage'),
      icon: <HistoryOutlined />,
      title: t('sidebar.general.history'),
      to: withWorkspace(ROUTES.HISTORY, { tab: 'builds' }),
    },
    {}, // empty item to create a gap
    ...(ability.can('access', 'GlobalSetupPage-googleAdManagerConnection')
      ? [GAM_ACCOUNT_ITEM]
      : []),
  ]
}
